<script setup>
import { nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import { useRoute } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { useAccount } from 'dashboard/composables/useAccount';
import { useKanbanCWBridge } from 'dashboard/composables/useKanbanCWBridge';
import Icon from 'next/icon/Icon.vue';

const props = defineProps({
  isCollapsed: {
    type: Boolean,
    default: false,
  },
});

const { t } = useI18n();
const { accountId } = useAccount();
const route = useRoute();
const {
  hasKanbanCWBridge,
  fetchSidebarItems,
  openSidebarItem,
  resolveActiveSidebarItem,
  onPanelStateChange,
} = useKanbanCWBridge();

const items = ref([]);
const activeItemKey = ref(null);

const syncActiveFromDom = () => {
  const routeItem = resolveActiveSidebarItem(route);
  if (routeItem) {
    activeItemKey.value = routeItem.key;
    return;
  }

  const activeItem = items.value.find(item => {
    const element = document.getElementById(item.menuId);
    return element?.classList.contains('active');
  });
  activeItemKey.value = activeItem?.key || null;
};

const loadItems = async () => {
  if (!hasKanbanCWBridge.value) {
    items.value = [];
    activeItemKey.value = null;
    return;
  }

  items.value = await fetchSidebarItems(accountId.value);
  await nextTick();
  syncActiveFromDom();
};

const handleItemClick = item => {
  openSidebarItem(item, accountId.value);
};

const resolveLabel = item => {
  const translationsByKey = {
    kanban: t('WORKSPACE_BRIDGE.SIDEBAR.KANBAN'),
    conexoes: t('WORKSPACE_BRIDGE.SIDEBAR.CONNECTIONS'),
    chats: t('WORKSPACE_BRIDGE.SIDEBAR.INTERNAL_CHAT'),
    projetos: t('WORKSPACE_BRIDGE.SIDEBAR.PROJECTS'),
    crm: t('WORKSPACE_BRIDGE.SIDEBAR.CRM'),
    atendimentos: t('WORKSPACE_BRIDGE.SIDEBAR.APPOINTMENTS'),
    campanhas: t('WORKSPACE_BRIDGE.SIDEBAR.CAMPAIGNS'),
    email: t('WORKSPACE_BRIDGE.SIDEBAR.EMAIL'),
    chatbot: t('WORKSPACE_BRIDGE.SIDEBAR.SMART_FLOWS'),
    permissions: t('WORKSPACE_BRIDGE.SIDEBAR.CONFIG_EXTRA'),
  };

  return translationsByKey[item.key] || item.labelDefault;
};

let removePanelStateListener = null;

onMounted(async () => {
  removePanelStateListener = onPanelStateChange(event => {
    activeItemKey.value = event.detail?.open
      ? event.detail?.itemKey || null
      : null;
  });

  await loadItems();
});

onBeforeUnmount(() => {
  removePanelStateListener?.();
});

watch(accountId, async () => {
  await loadItems();
});

watch(
  () => props.isCollapsed,
  async () => {
    await nextTick();
    syncActiveFromDom();
  }
);

watch(
  () => route.fullPath,
  async () => {
    await nextTick();
    syncActiveFromDom();
  }
);
</script>

<template>
  <li
    v-for="item in items"
    :key="item.key"
    class="grid gap-1 text-sm cursor-pointer select-none min-w-0"
  >
    <!-- Custom product bridge: native sidebar item in Chatwoot opening KanbanCW routes. -->
    <button
      :id="item.menuId"
      data-kbcw-native-sidebar="1"
      type="button"
      class="flex items-center min-w-0 rounded-lg transition-all duration-100 ease-out text-n-slate-11 hover:bg-n-alpha-2"
      :class="[
        isCollapsed ? 'justify-center size-10' : 'gap-2 px-1.5 py-1 h-8 w-full',
        {
          'text-n-slate-12 bg-n-alpha-2 font-medium':
            activeItemKey === item.key,
        },
      ]"
      :title="resolveLabel(item)"
      @click="handleItemClick(item)"
    >
      <div class="cw-menu-inner flex items-center min-w-0 flex-1">
        <span
          class="menu-icon relative inline-flex items-center justify-center shrink-0"
        >
          <Icon :icon="item.icon" class="size-4" />
        </span>
        <span v-if="!isCollapsed" class="cw-menu-label truncate ml-2 text-sm">
          {{ resolveLabel(item) }}
        </span>
      </div>
    </button>
  </li>
</template>
